import { isInteger, range } from "lodash";
import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import colors from "tailwindcss/colors";
import {
  AssessmentQuestionComment,
  AssessmentQuestionType,
  AssessmentType,
  CalibrationNodeFragment,
  GoalState,
  GoalVisibility,
  NpsAnswerValue,
} from "types/graphql-schema";
import { BasicUser, TFLocationState } from "types/topicflow";

import ArtifactDueDate from "@apps/artifact/components/artifact-due-date";
import EngagementSurveyQuestionBarChart from "@apps/engagement-survey/components/delivery/engagement-survey-question-bar-chart";
import EngagementSurveyQuestionNpsChart from "@apps/engagement-survey/components/delivery/engagement-survey-question-nps-chart";
import EngagementSurveyQuestionPieChart from "@apps/engagement-survey/components/delivery/engagement-survey-question-pie-chart";
import { UseLabelType } from "@apps/use-label/use-label";
import useUiPreferenceCache, {
  getUiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button, { ButtonSize } from "@components/button/button";
import CoreValueIcon from "@components/core-value-icon/core-value-icon";
import DraftLabel from "@components/draft-label/draft-label";
import GoalIcon from "@components/goal-icon/goal-icon";
import GoalProgressPill from "@components/goal-progress-pill/goal-progress-pill";
import GoalVisibilityIcon from "@components/goal-visibility-icon/goal-visibility-icon";
import AppLink from "@components/link/link";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { BAR_CHART_COLORS, PIE_CHART_COLORS } from "@helpers/charts";
import {
  assertEdgesNonNull,
  assertNonNull,
  getUrlUsingTypename,
} from "@helpers/helpers";

import AssessmentQuestionMultiSelectOptions from "../assessment-question-multi-select-options";
import AssessmentQuestionMultiSelectOptionsCombined from "../assessment-question-multi-select-options-combined";
import AssessmentQuestionRangeOptionsCombined from "../assessment-question-range-options-combined";
import AssessmentQuestionStackedTextAnswers from "../assessment-question-stacked-text-answers";
import { AssessmentSidebarTab } from "../assessment/assessment";
import QuestionCalibrationSection from "../calibration/question-calibration-section";
import { assessmentContextSidebarVar } from "../context-sidebar/context-sidebar";
import RoleBasedAssessmentGoalPopover from "../delivery/role-based-assessment-goal-popover";
import PrevAnswerArrow from "../prev-answer-arrow";
import PrevAnswerChip from "../prev-answer-chip";
import CoreValuesQuestionRelatedRecognition from "./core-values-question-related-recognition";
import NpsAssessmentQuestionContent from "./nps-assessment-question-content";
import OverallCoreValuesAssessmentQuestionDescription from "./overall-core-values-assessment-question-description";
import OverallGoalAssessmentQuestionDescription from "./overall-goal-assessment-question-description";
import QuestionLockedByPrecalibration from "./question-locked-by-precalibration";
import QuestionVisibilityIcon from "./question-visibility-icon";
import RangeAssessmentQuestionContent from "./range-assessment-question-content";
import TextAssessmentQuestionContent from "./text-assessment-question-content";
import {
  AssessmentAnswer,
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestion,
  CompetencyAssessmentQuestion,
  CompetencyCriteriaAssessmentQuestion,
  IndividualGoalAssessmentQuestion,
  IndividualRecognitionCoreValueAssessmentQuestion,
  MultiChoiceAssessmentQuestion,
  NpsAssessmentQuestion,
  OverallGoalAssessmentQuestion,
  OverallRecognitionCoreValueAssessmentQuestion,
  RangeAssessmentQuestion,
  ResponsibilityAssessmentQuestion,
  TalentIndicatorAssessmentQuestion,
} from "./types";

type QuestionInterfaceOpts = {
  label: UseLabelType;
};

type QuestionFormProps = {
  formDisabled: boolean;
  assessmentType: AssessmentType;
  answer?: AssessmentAnswer;
  onUpdateAnswer: (newAnswer: AssessmentAnswer) => void;
  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  target?: BasicUser | null;
  calibrations?: CalibrationNodeFragment[];
};

type QuestionDeliveryProps = {
  answer: AssessmentAnswerWithResponderAndId;
  calibrations?: CalibrationNodeFragment[];
};

type QuestionDeliveryReadonlyProps = {
  answers: AssessmentAnswerWithResponderAndId[];
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  target?: BasicUser | null;
  calibrations?: CalibrationNodeFragment[];
};

type QuestionTitleRightSideProps = {
  target?: BasicUser | null;
  assessmentType?: AssessmentType;
  mostRecentAnswer?: { integerAnswer: number; submitted: string };
  currentAnswer?: number;
};

interface AssessmentQuestionInterface {
  getTitle(): ReactNode;
  getTitleRightSide(opts: QuestionTitleRightSideProps): ReactNode | null;
  getDescription(): ReactNode;
  getForm(opts: QuestionFormProps): ReactNode;
  getDelivery(opts: QuestionDeliveryProps): ReactNode;
  getDeliveryReadonly(opts: QuestionDeliveryReadonlyProps): ReactNode;
  getEngagementSurveyDelivery(opts: QuestionDeliveryReadonlyProps): ReactNode;
  getDeliveryComments(opts: QuestionDeliveryReadonlyProps): ReactNode;
  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean;
  hasEnteredComment(answer?: AssessmentAnswer | null): boolean;
}

interface QuestionConstructor {
  new (
    data: AssessmentQuestion,
    opts: QuestionInterfaceOpts
  ): AssessmentQuestionInterface;
}

export const getQuestionLabels = (question: {
  startValue: number;
  endValue: number;
  labels: ReadonlyArray<string | null>;
}) => {
  return range(question.startValue, question.endValue + 1).reduce(
    (acc, value, index) => ({
      ...acc,
      [value]: question.labels[index] || value.toString(),
    }),
    {} as Record<string, string>
  );
};

class BaseQuestion implements AssessmentQuestionInterface {
  constructor(public data: AssessmentQuestion) {}

  _getVisibilityLabel(target?: BasicUser | null): ReactNode {
    const currentUser = currentUserVar();

    if (!target || target.id === currentUser.id) {
      return null;
    }
    return <QuestionVisibilityIcon visibility={this.data.responseVisibility} />;
  }

  getTitleRightSide(opts: QuestionTitleRightSideProps): ReactNode {
    return (
      <div className="flex items-center gap-2">
        {opts.assessmentType === AssessmentType.Performance &&
          opts.mostRecentAnswer &&
          !opts.currentAnswer &&
          this.data.__typename === "TalentIndicatorAssessmentQuestionNode" && (
            <PrevAnswerChip
              question={this.data}
              prevAnswer={opts.mostRecentAnswer}
            />
          )}
        {opts.assessmentType === AssessmentType.Performance &&
          opts.mostRecentAnswer &&
          opts.currentAnswer &&
          this.data.__typename === "TalentIndicatorAssessmentQuestionNode" && (
            <PrevAnswerArrow
              currentIntegerAnswer={opts.currentAnswer}
              question={this.data}
              prevAnswer={opts.mostRecentAnswer}
            />
          )}
        {opts.assessmentType === AssessmentType.EngagementSurvey
          ? null
          : this._getVisibilityLabel(opts.target)}
      </div>
    );
  }

  getTitle(): ReactNode {
    return <div className="flex-1 tf-header-base-dark">{this.data.title}</div>;
  }

  getDescription(): ReactNode {
    return (
      !isEmptyValue(this.data.description) && (
        <TextareaWysiwyg
          className="text-gray-500 mb-4"
          value={this.data.description}
          fullWidth
        />
      )
    );
  }

  getForm(opts: QuestionFormProps): ReactNode {
    return <div className="font-bold text-red-600">Not implemented</div>;
  }

  getDelivery(opts: QuestionDeliveryProps): ReactNode {
    return <div className="font-bold text-red-600">Not implemented</div>;
  }

  getDeliveryReadonly(opts: QuestionDeliveryReadonlyProps): ReactNode {
    return <div className="font-bold text-red-600">Not implemented</div>;
  }

  getEngagementSurveyDelivery(opts: QuestionDeliveryReadonlyProps): ReactNode {
    return <div className="font-bold text-red-600">Not implemented</div>;
  }

  getDeliveryComments(opts: QuestionDeliveryReadonlyProps): ReactNode {
    return <div className="font-bold text-red-600">Not implemented</div>;
  }

  isQuestionAnswered(): boolean {
    throw new Error("Method not implemented.");
  }

  hasEnteredComment(answer?: AssessmentAnswer | null): boolean {
    return (
      this.data.comment === AssessmentQuestionComment.Optional ||
      this.data.comment === AssessmentQuestionComment.NoComment ||
      !isEmptyValue(answer?.comment ?? "{}")
    );
  }
}

class RangeQuestion extends BaseQuestion {
  data: RangeAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (data.__typename !== "RangeAssessmentQuestionNode") {
      throw new Error("Invalid question type in RangeQuestion constructor");
    }
    this.data = data;
  }

  labels() {
    return getQuestionLabels(this.data);
  }

  getForm({
    answer,
    assessmentType,
    onUpdateAnswer,
    formDisabled,
    otherParticipantsAnswers,
    calibrations,
  }: QuestionFormProps): ReactNode {
    const labels = this.labels();
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <>
        <div className="px-6 pb-6">
          {answer?.isLocked && (
            <div className="mb-6">
              <QuestionLockedByPrecalibration />
            </div>
          )}
          <RangeAssessmentQuestionContent
            disabled={formDisabled}
            onlyRangeOptionsDisabled={answer?.isLocked}
            answer={answer}
            otherParticipantsAnswers={
              assessmentType === AssessmentType.Performance
                ? otherParticipantsAnswers
                : undefined
            }
            question={this.data}
            onChangeAnswer={(newAnswer) => {
              onUpdateAnswer({
                questionId: this.data.id,
                ...answer,
                ...newAnswer,
                textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
                comment: newAnswer.comment ?? answer?.comment ?? "{}",
              });
            }}
          />
          {commentItems.length > 0 &&
            assessmentType === AssessmentType.Performance && (
              <div className="mt-4">
                <AssessmentQuestionStackedTextAnswers
                  questionId={this.data.id}
                  answers={commentItems}
                />
              </div>
            )}
        </div>
        {calibrations && calibrations.length > 0 && (
          <div className="border-t p-6">
            <QuestionCalibrationSection
              calibrations={calibrations}
              questionLabels={labels}
            />
          </div>
        )}
      </>
    );
  }

  getDelivery({ answer, calibrations }: QuestionDeliveryProps): ReactNode {
    const labels = this.labels();
    return (
      <>
        <div className="px-6 pb-6">
          <RangeAssessmentQuestionContent
            disabled
            answer={answer}
            calibrations={calibrations}
            question={this.data}
          />
        </div>
        {calibrations && calibrations.length > 0 && (
          <div className="border-t p-6">
            <QuestionCalibrationSection
              calibrations={calibrations}
              questionLabels={labels}
            />
          </div>
        )}
      </>
    );
  }

  getDeliveryReadonly({
    answers,
    calibrations,
  }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentsContent = this.getDeliveryComments({ answers });
    const labels = this.labels();
    return (
      <>
        <div className="px-6 pb-6">
          <AssessmentQuestionRangeOptionsCombined
            answers={answers}
            calibrations={calibrations}
            startValue={this.data.startValue}
            endValue={this.data.endValue}
            labels={this.data.labels}
            labelDescriptions={this.data.labelDescriptions}
          />
          {commentsContent && <div className="mt-6">{commentsContent}</div>}
        </div>
        {calibrations && calibrations.length > 0 && (
          <div className="border-t p-6">
            <QuestionCalibrationSection
              calibrations={calibrations}
              questionLabels={labels}
            />
          </div>
        )}
      </>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = this.labels();
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return commentItems.length > 0 ? (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={commentItems}
      />
    ) : null;
  }

  getEngagementSurveyDelivery({
    answers,
  }: QuestionDeliveryReadonlyProps): ReactNode {
    const chartData = range(
      this.data.endValue,
      this.data.startValue - 1,
      -1
    ).map((value, index) => ({
      name: this.data.labels[value - this.data.startValue] || value.toString(),
      value: answers.filter((answer) => answer.integerAnswer === value).length,
      color: PIE_CHART_COLORS[index % PIE_CHART_COLORS.length],
    }));
    return <EngagementSurveyQuestionPieChart data={chartData} />;
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }
}
class NpsQuestion extends BaseQuestion {
  data: NpsAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (data.__typename !== "NpsAssessmentQuestionNode") {
      throw new Error("Invalid question type in NpsQuestion constructor");
    }
    this.data = data;
  }

  getForm({
    answer,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <NpsAssessmentQuestionContent
          disabled={formDisabled}
          answer={answer}
          question={this.data}
          onChangeAnswer={(newAnswer) => {
            onUpdateAnswer({
              questionId: this.data.id,
              ...answer,
              ...newAnswer,
              textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
              comment: newAnswer.comment ?? answer?.comment ?? "{}",
            });
          }}
        />
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <NpsAssessmentQuestionContent
        disabled
        answer={answer}
        question={this.data}
      />
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentsContent = this.getDeliveryComments({ answers });
    return (
      <>
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={0}
          endValue={10}
          labels={["", "", "", "", "", "", "", "", "", ""]}
          labelDescriptions={["", "", "", "", "", "", "", "", "", ""]}
        />
        {commentsContent && <div className="mt-6">{commentsContent}</div>}
      </>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? answer.integerAnswer?.toString()
          : null,
      }));
    return commentItems.length > 0 ? (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={commentItems}
      />
    ) : null;
  }

  getEngagementSurveyDelivery({
    answers,
  }: QuestionDeliveryReadonlyProps): ReactNode {
    const chartData = [
      {
        name: "Promoters (9-10)",
        value: answers.filter(
          (answer) =>
            isInteger(answer.integerAnswer) &&
            answer.npsValue === NpsAnswerValue.Promoter
        ).length,
        color: colors.emerald[500],
      },
      {
        name: "Passives (7-8)",
        value: answers.filter(
          (answer) =>
            isInteger(answer.integerAnswer) &&
            answer.npsValue === NpsAnswerValue.Passive
        ).length,
        color: colors.amber[500],
      },
      {
        name: "Detractors (0-6)",
        value: answers.filter(
          (answer) =>
            isInteger(answer.integerAnswer) &&
            answer.npsValue === NpsAnswerValue.Detractor
        ).length,
        color: colors.red[500],
      },
    ];
    return <EngagementSurveyQuestionNpsChart data={chartData} />;
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }
}

class MultichoiceQuestion extends BaseQuestion {
  data: MultiChoiceAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (data.__typename !== "MultiChoiceAssessmentQuestionNode") {
      throw new Error(
        "Invalid question type in MultichoiceQuestion constructor"
      );
    }
    this.data = data;
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue:
              answer.choices && answer.choices.length > 0
                ? answer.choices
                    .map((choice) =>
                      choice !== null ? this.data.options[choice] : ""
                    )
                    .join(", ")
                : null,
          }))
      : [];
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionMultiSelectOptions
          choices={answer?.choices}
          disabled={formDisabled}
          options={this.data.options}
          otherParticipantsAnswers={
            assessmentType === AssessmentType.Performance
              ? otherParticipantsAnswers
              : undefined
          }
          optionDescriptions={this.data.optionDescriptions}
          onClickAnswer={(newChoices) =>
            onUpdateAnswer({
              questionId: this.data.id,
              integerAnswer: null,
              textAnswer: answer?.textAnswer ?? "{}",
              choices: newChoices,
              comment: answer?.comment ?? "{}",
            })
          }
        />
        {answer &&
          this.data.comment !== AssessmentQuestionComment.NoComment &&
          (!formDisabled || !isEmptyValue(answer.comment)) && (
            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="text-gray-500 text-xs uppercase font-semibold">
                  Comment{" "}
                  {this.data.comment === AssessmentQuestionComment.Required
                    ? "*"
                    : ""}
                </div>
              </div>
              <TextareaWysiwyg
                editable={!formDisabled}
                placeholder="Add a comment..."
                className="mt-1 bg-white"
                fullWidth
                value={answer.comment ?? "{}"}
                onChangeValue={(comment) =>
                  onUpdateAnswer({
                    questionId: this.data.id,
                    integerAnswer: null,
                    textAnswer: answer.textAnswer ?? "{}",
                    choices: answer.choices,
                    comment,
                  })
                }
              />
            </div>
          )}
        {commentItems.length > 0 &&
          assessmentType === AssessmentType.Performance && (
            <div className="mt-4">
              <AssessmentQuestionStackedTextAnswers
                questionId={this.data.id}
                answers={commentItems}
              />
            </div>
          )}
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionMultiSelectOptions
          choices={answer.choices}
          disabled
          options={this.data.options}
          optionDescriptions={this.data.optionDescriptions}
        />
        {answer && !isEmptyValue(answer.comment) && (
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-gray-500 text-xs uppercase font-semibold">
                Comment
              </div>
            </div>
            <TextareaWysiwyg
              editable={false}
              className="mt-1 bg-white"
              fullWidth
              value={answer.comment ?? "{}"}
            />
          </div>
        )}
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentsContent = this.getDeliveryComments({ answers });
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionMultiSelectOptionsCombined
          answers={answers}
          options={this.data.options}
          optionDescriptions={this.data.optionDescriptions}
        />
        {commentsContent && <div className="mt-6">{commentsContent}</div>}
      </div>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue:
          answer.choices && answer.choices.length > 0
            ? answer.choices
                .map((choice) =>
                  choice !== null ? this.data.options[choice] : ""
                )
                .join(", ")
            : null,
      }));
    return commentItems.length > 0 ? (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={commentItems}
      />
    ) : null;
  }

  getEngagementSurveyDelivery({
    answers,
  }: QuestionDeliveryReadonlyProps): ReactNode {
    const chartData = this.data.options.map((name, index) => ({
      name: name ?? "",
      value: answers.filter(
        (answer) => answer.choices && answer.choices.includes(index)
      ).length,
      color: BAR_CHART_COLORS[index % BAR_CHART_COLORS.length],
    }));
    return (
      <EngagementSurveyQuestionBarChart
        sorted
        data={chartData}
        totalResponders={answers.length}
      />
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return (
      !this.data.isResponseMandatory ||
      (!!answer?.choices && answer.choices.length > 0)
    );
  }
}

const TextQuestionForm = ({
  formDisabled,
  answer,
  onUpdateAnswer,
  questionId,
  responseItems,
  assessmentType,
}: {
  formDisabled: boolean;
  answer?: AssessmentAnswer | null;
  onUpdateAnswer: (newAnswer: Partial<AssessmentAnswer>) => void;
  questionId: number;
  responseItems: {
    responder?: BasicUser | null;
    text?: string | null;
  }[];
  assessmentType: AssessmentType;
}) => {
  const organization = currentOrganizationVar();
  const { saveUiPreference } = useUiPreferenceCache();
  return (
    <>
      <TextAssessmentQuestionContent
        disabled={formDisabled}
        answer={answer}
        onChangeAnswer={onUpdateAnswer}
        onFocus={() => {
          const uiPreferenceCache = getUiPreferenceCache();
          if (
            organization.featureFlags.reviewContextGeneration &&
            waffle.flag_is_active("review-context-sidebar") &&
            uiPreferenceCache.assessmentSidebarSelectedTab ===
              AssessmentSidebarTab.context
          ) {
            assessmentContextSidebarVar({
              questionId: questionId,
              showSuggestedResponses: true,
            });
          }
        }}
      />
      {organization.featureFlags.reviewContextGeneration &&
        waffle.flag_is_active("review-context-sidebar") && (
          <div className="mt-4 justify-end hidden lg:flex">
            <Button
              onClick={() => {
                saveUiPreference({ assessmentSidebarExpanded: true });
                assessmentContextSidebarVar({
                  questionId: questionId,
                  showSuggestedResponses: true,
                });
              }}
              size={ButtonSize.medium}
            >
              <TopicflowIcon type="ai" className="h-3 w-3 text-gray-500" />
              Context
            </Button>
          </div>
        )}
      {responseItems.length > 0 &&
        assessmentType === AssessmentType.Performance && (
          <div className="mt-4">
            <AssessmentQuestionStackedTextAnswers
              questionId={questionId}
              answers={responseItems}
            />
          </div>
        )}
    </>
  );
};

class TextQuestion extends BaseQuestion {
  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const responseItems = otherParticipantsAnswers
      ? otherParticipantsAnswers.map((answer) => ({
          responder: answer.responder,
          text: answer.textAnswer,
        }))
      : [];

    return (
      <div className="px-6 pb-6">
        <TextQuestionForm
          formDisabled={formDisabled}
          answer={answer}
          onUpdateAnswer={(newAnswer) =>
            onUpdateAnswer({
              questionId: this.data.id,
              integerAnswer: null,
              choices: answer?.choices,
              textAnswer: null,
              comment: null,
              ...newAnswer,
            })
          }
          questionId={this.data.id}
          responseItems={responseItems}
          assessmentType={assessmentType}
        />
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <TextareaWysiwyg
          key={answer.id}
          editable={false}
          className="bg-white max-w-none"
          value={answer.textAnswer ?? "{}"}
          deps={[answer.id]}
        />
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const responseItems = answers.map((answer) => ({
      responder: answer.responder,
      text: answer.textAnswer,
      answerId: answer.id,
      inferredSources: answer.inferredSources,
    }));
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionStackedTextAnswers
          questionId={this.data.id}
          answers={responseItems}
          showContextSidebarCTA={true}
        />
      </div>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    return null;
  }

  getEngagementSurveyDelivery({
    answers,
  }: QuestionDeliveryReadonlyProps): ReactNode {
    const responseItems = answers.map((answer) => ({
      responder: answer.responder,
      text: answer.textAnswer,
    }));
    return (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={responseItems}
        viewMoreEnabled
      />
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return (
      !this.data.isResponseMandatory ||
      !isEmptyValue(answer?.textAnswer ?? "{}")
    );
  }
}

class CompetencyQuestion extends BaseQuestion {
  data: CompetencyAssessmentQuestion;

  constructor(data: AssessmentQuestion, opts: QuestionInterfaceOpts) {
    super(data);
    if (data.__typename !== "CompetencyAssessmentQuestionNode") {
      throw new Error(
        "Invalid question type in CompetencyQuestion constructor"
      );
    }
    this.data = data;
  }

  getTitleRightSide(opts: QuestionTitleRightSideProps): ReactNode {
    const visibilityLabel = this._getVisibilityLabel(opts?.target);
    if (this.data.competency && opts?.target) {
      return (
        <div className="flex items-center justify-between gap-2 empty:hidden">
          {this.data.competency && opts.target && (
            <RoleBasedAssessmentGoalPopover
              competency={this.data.competency}
              role={this.data.role}
              target={opts.target}
            />
          )}
          {visibilityLabel ? visibilityLabel : null}
        </div>
      );
    }
    return <div className="flex empty:hidden">{visibilityLabel}</div>;
  }

  getTitle(): ReactNode {
    return <div className="flex-1 tf-header-base-dark">{this.data.title}</div>;
  }

  getDescription(): ReactNode {
    const criteria = assertEdgesNonNull(this.data.competency?.criteria);
    return (
      <>
        {!isEmptyValue(this.data.description) && (
          <TextareaWysiwyg
            className="text-gray-500 mb-4"
            value={this.data.description}
            fullWidth
          />
        )}
        <div className="mt-2">
          {criteria.map((c) => (
            <TextareaWysiwyg
              key={c.id}
              editable={false}
              value={c.content}
              fullWidth
            />
          ))}
        </div>
      </>
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled={formDisabled}
          answer={answer}
          otherParticipantsAnswers={
            assessmentType === AssessmentType.Performance
              ? otherParticipantsAnswers
              : undefined
          }
          question={this.data}
          onChangeAnswer={(newAnswer) => {
            onUpdateAnswer({
              questionId: this.data.id,
              ...answer,
              ...newAnswer,
              textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
              comment: newAnswer.comment ?? answer?.comment ?? "{}",
            });
          }}
        />
        {commentItems.length > 0 &&
          assessmentType === AssessmentType.Performance && (
            <div className="mt-4">
              <AssessmentQuestionStackedTextAnswers
                questionId={this.data.id}
                answers={commentItems}
              />
            </div>
          )}
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled
          answer={answer}
          question={this.data}
        />
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentsContent = this.getDeliveryComments({ answers });
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={this.data.startValue}
          endValue={this.data.endValue}
          labels={this.data.labels}
          labelDescriptions={this.data.labelDescriptions}
        />
        {commentsContent && <div className="mt-6">{commentsContent}</div>}
      </div>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return commentItems.length > 0 ? (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={commentItems}
      />
    ) : null;
  }
}

class CompetencyCriteriaQuestion extends BaseQuestion {
  data: CompetencyCriteriaAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (data.__typename !== "CompetencyCriteriaAssessmentQuestionNode") {
      throw new Error(
        "Invalid question type in CompetencyCriteriaQuestion constructor"
      );
    }
    this.data = data;
  }

  getTitle(): ReactNode {
    return (
      <TextareaWysiwyg
        editable={false}
        className="tf-header-base-dark flex-1"
        value={this.data.competencyCriteria?.content}
        fullWidth
      />
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled={formDisabled}
          answer={answer}
          otherParticipantsAnswers={
            assessmentType === AssessmentType.Performance
              ? otherParticipantsAnswers
              : undefined
          }
          question={this.data}
          onChangeAnswer={(newAnswer) => {
            onUpdateAnswer({
              questionId: this.data.id,
              ...answer,
              ...newAnswer,
              textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
              comment: newAnswer.comment ?? answer?.comment ?? "{}",
            });
          }}
        />
        {commentItems.length > 0 &&
          assessmentType === AssessmentType.Performance && (
            <div className="mt-4">
              <AssessmentQuestionStackedTextAnswers
                questionId={this.data.id}
                answers={commentItems}
              />
            </div>
          )}
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled
          answer={answer}
          question={this.data}
        />
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentsContent = this.getDeliveryComments({ answers });
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={this.data.startValue}
          endValue={this.data.endValue}
          labels={this.data.labels}
          labelDescriptions={this.data.labelDescriptions}
        />
        {commentsContent && <div className="mt-6">{commentsContent}</div>}
      </div>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return commentItems.length > 0 ? (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={commentItems}
      />
    ) : null;
  }
}

class ResponsibilityQuestion extends BaseQuestion {
  data: ResponsibilityAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (data.__typename !== "ResponsibilityAssessmentQuestionNode") {
      throw new Error(
        "Invalid question type in ResponsibilityQuestion constructor"
      );
    }
    this.data = data;
  }

  getTitle(): ReactNode {
    return (
      <TextareaWysiwyg
        editable={false}
        className="flex-1 tf-header-base-dark"
        value={this.data.responsibility?.content}
        fullWidth
      />
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled={formDisabled}
          answer={answer}
          otherParticipantsAnswers={
            assessmentType === AssessmentType.Performance
              ? otherParticipantsAnswers
              : undefined
          }
          question={this.data}
          onChangeAnswer={(newAnswer) => {
            onUpdateAnswer({
              questionId: this.data.id,
              ...answer,
              ...newAnswer,
              textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
              comment: newAnswer.comment ?? answer?.comment ?? "{}",
            });
          }}
        />
        {commentItems.length > 0 &&
          assessmentType === AssessmentType.Performance && (
            <div className="mt-4">
              <AssessmentQuestionStackedTextAnswers
                questionId={this.data.id}
                answers={commentItems}
              />
            </div>
          )}
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled
          answer={answer}
          question={this.data}
        />
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentsContent = this.getDeliveryComments({ answers });
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={this.data.startValue}
          endValue={this.data.endValue}
          labels={this.data.labels}
          labelDescriptions={this.data.labelDescriptions}
        />
        {commentsContent && <div className="mt-6">{commentsContent}</div>}
      </div>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return commentItems.length > 0 ? (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={commentItems}
      />
    ) : null;
  }
}

const IndividualGoalQuestionLink = ({
  data,
}: {
  data: IndividualGoalAssessmentQuestion;
}) => {
  const location = useLocation<TFLocationState>();
  if (!data.goal) {
    return null;
  }
  return (
    <AppLink
      to={getUrlUsingTypename(data.goal, location)}
      className="tf-header-base-dark hover:underline"
    >
      {data.goal.title}{" "}
      {data.goal.goalVisibility === GoalVisibility.Private && (
        <GoalVisibilityIcon
          className="inline"
          goalVisibility={data.goal.goalVisibility}
          size="4"
        />
      )}{" "}
      {data.goal.state === GoalState.Draft && <DraftLabel />}
    </AppLink>
  );
};

class IndividualGoalQuestion extends BaseQuestion {
  data: IndividualGoalAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (data.__typename !== "IndividualGoalAssessmentQuestionNode") {
      throw new Error(
        "Invalid question type in IndividualGoalQuestion constructor"
      );
    }
    this.data = data;
  }

  getTitle(): ReactNode {
    if (!this.data.goal) {
      return null;
    }
    return (
      <div className="flex flex-1 justify-between">
        <div className="flex gap-1 leading-[24px]">
          <GoalIcon
            scope={this.data.goal.scope}
            size={5}
            state={this.data.goal.state}
            className="mt-0.5"
          />
          <IndividualGoalQuestionLink data={this.data} />
        </div>
        <div className="mt-0.5 shrink-0">
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-500">
              <ArtifactDueDate artifact={this.data.goal} />
            </div>
            <GoalProgressPill
              progress={this.data.goal.progress}
              status={this.data.goal.status}
              state={this.data.goal.state}
              isStale={this.data.goal.isStale}
            />
          </div>
        </div>
      </div>
    );
  }

  getDescription(): ReactNode {
    return null;
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled={formDisabled}
          answer={answer}
          otherParticipantsAnswers={
            assessmentType === AssessmentType.Performance
              ? otherParticipantsAnswers
              : undefined
          }
          question={this.data}
          onChangeAnswer={(newAnswer) => {
            onUpdateAnswer({
              questionId: this.data.id,
              ...answer,
              ...newAnswer,
              textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
              comment: newAnswer.comment ?? answer?.comment ?? "{}",
            });
          }}
        />
        {commentItems.length > 0 &&
          assessmentType === AssessmentType.Performance && (
            <div className="mt-4">
              <AssessmentQuestionStackedTextAnswers
                questionId={this.data.id}
                answers={commentItems}
              />
            </div>
          )}
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled
          answer={answer}
          question={this.data}
        />
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? answer.integerAnswer?.toString()
          : null,
      }));
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={this.data.startValue}
          endValue={this.data.endValue}
          labels={this.data.labels}
          labelDescriptions={this.data.labelDescriptions}
        />
        {commentItems.length > 0 && (
          <div className="mt-6">
            <AssessmentQuestionStackedTextAnswers
              questionId={this.data.id}
              answers={commentItems}
            />
          </div>
        )}
      </div>
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }
}

class IndividualRecognitionCoreValueQuestion extends BaseQuestion {
  data: IndividualRecognitionCoreValueAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (
      data.__typename !== "IndividualRecognitionCoreValueAssessmentQuestionNode"
    ) {
      throw new Error(
        "Invalid question type in IndividualRecogntionCoreValueQuestion constructor"
      );
    }
    this.data = data;
  }

  getTitle(): ReactNode {
    if (!this.data.coreValue) {
      return null;
    }
    return (
      <div className="flex-1 flex items-center gap-2">
        <CoreValueIcon
          imagePxSize={20}
          image={this.data.coreValue.image}
          emoji={this.data.coreValue.emoji}
        />
        <div className="tf-header-base-dark">{this.data.coreValue.title}</div>
      </div>
    );
  }

  getDescription(): ReactNode {
    return (
      <div className="mt-1 mb-4 text-sm text-gray-500 whitespace-pre-line">
        {this.data.coreValue?.description}
      </div>
    );
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
    periodStartDate,
    periodEndDate,
    target,
  }: QuestionFormProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <>
        <div className="px-6 pb-6">
          <RangeAssessmentQuestionContent
            disabled={formDisabled}
            answer={answer}
            otherParticipantsAnswers={
              assessmentType === AssessmentType.Performance
                ? otherParticipantsAnswers
                : undefined
            }
            question={this.data}
            onChangeAnswer={(newAnswer) => {
              onUpdateAnswer({
                questionId: this.data.id,
                ...answer,
                ...newAnswer,
                textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
                comment: newAnswer.comment ?? answer?.comment ?? "{}",
              });
            }}
          />
          {commentItems.length > 0 &&
            assessmentType === AssessmentType.Performance && (
              <div className="mt-4">
                <AssessmentQuestionStackedTextAnswers
                  questionId={this.data.id}
                  answers={commentItems}
                />
              </div>
            )}
        </div>
        {this.data.coreValue && target && (
          <div className="border-t p-6">
            <CoreValuesQuestionRelatedRecognition
              coreValueId={this.data.coreValue.id}
              targetId={target.id}
              periodStartDate={periodStartDate}
              periodEndDate={periodEndDate}
            />
          </div>
        )}
      </>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled
          answer={answer}
          question={this.data}
        />
      </div>
    );
  }

  getDeliveryReadonly({
    answers,
    target,
    periodStartDate,
    periodEndDate,
  }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={this.data.startValue}
          endValue={this.data.endValue}
          labels={this.data.labels}
          labelDescriptions={this.data.labelDescriptions}
        />
        {commentItems.length > 0 && (
          <div className="mt-6">
            <AssessmentQuestionStackedTextAnswers
              questionId={this.data.id}
              answers={commentItems}
            />
          </div>
        )}
        {this.data.coreValue && target && (
          <div className="mt-6 border-t pt-6">
            <CoreValuesQuestionRelatedRecognition
              coreValueId={this.data.coreValue.id}
              targetId={target.id}
              periodStartDate={periodStartDate}
              periodEndDate={periodEndDate}
            />
          </div>
        )}
      </div>
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }
}

class OverallGoalQuestion extends BaseQuestion {
  data: OverallGoalAssessmentQuestion;
  label: UseLabelType;

  constructor(data: AssessmentQuestion, opts: QuestionInterfaceOpts) {
    super(data);
    if (data.__typename !== "OverallGoalAssessmentQuestionNode") {
      throw new Error(
        "Invalid question type in OverallGoalQuestion constructor"
      );
    }
    this.data = data;
    this.label = opts.label;
  }

  getTitle(): ReactNode {
    return (
      <div className="flex-1 tf-header-base-dark">
        Provide an overall assessment on{" "}
        {this.label("goal", { pluralize: true })}
      </div>
    );
  }

  getDescription(): ReactNode {
    return <OverallGoalAssessmentQuestionDescription className="mb-4" />;
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled={formDisabled}
          answer={answer}
          otherParticipantsAnswers={
            assessmentType === AssessmentType.Performance
              ? otherParticipantsAnswers
              : undefined
          }
          question={this.data}
          onChangeAnswer={(newAnswer) => {
            onUpdateAnswer({
              questionId: this.data.id,
              ...answer,
              ...newAnswer,
              textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
              comment: newAnswer.comment ?? answer?.comment ?? "{}",
            });
          }}
        />
        {commentItems.length > 0 &&
          assessmentType === AssessmentType.Performance && (
            <div className="mt-4">
              <AssessmentQuestionStackedTextAnswers
                questionId={this.data.id}
                answers={commentItems}
              />
            </div>
          )}
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled
          answer={answer}
          question={this.data}
        />
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={this.data.startValue}
          endValue={this.data.endValue}
          labels={this.data.labels}
          labelDescriptions={this.data.labelDescriptions}
        />
        {commentItems.length > 0 && (
          <div className="mt-6">
            <AssessmentQuestionStackedTextAnswers
              questionId={this.data.id}
              answers={commentItems}
            />
          </div>
        )}
      </div>
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }
}

class OverallRecognitionCoreValueQuestion extends BaseQuestion {
  data: OverallRecognitionCoreValueAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (
      data.__typename !== "OverallRecognitionCoreValueAssessmentQuestionNode"
    ) {
      throw new Error(
        "Invalid question type in OverallRecognitionCoreValueQuestion constructor"
      );
    }
    this.data = data;
  }

  getTitle(): ReactNode {
    return (
      <div className="flex-1 tf-header-base-dark">
        Provide an overall assessment on Core Values
      </div>
    );
  }

  getDescription(): ReactNode {
    return <OverallCoreValuesAssessmentQuestionDescription className="mb-4" />;
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
  }: QuestionFormProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled={formDisabled}
          answer={answer}
          otherParticipantsAnswers={
            assessmentType === AssessmentType.Performance
              ? otherParticipantsAnswers
              : undefined
          }
          question={this.data}
          onChangeAnswer={(newAnswer) => {
            onUpdateAnswer({
              questionId: this.data.id,
              ...answer,
              ...newAnswer,
              textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
              comment: newAnswer.comment ?? answer?.comment ?? "{}",
            });
          }}
        />
        {commentItems.length > 0 &&
          assessmentType === AssessmentType.Performance && (
            <div className="mt-4">
              <AssessmentQuestionStackedTextAnswers
                questionId={this.data.id}
                answers={commentItems}
              />
            </div>
          )}
      </div>
    );
  }

  getDelivery({ answer }: QuestionDeliveryProps): ReactNode {
    return (
      <div className="px-6 pb-6">
        <RangeAssessmentQuestionContent
          disabled
          answer={answer}
          question={this.data}
        />
      </div>
    );
  }

  getDeliveryReadonly({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return (
      <div className="px-6 pb-6">
        <AssessmentQuestionRangeOptionsCombined
          answers={answers}
          startValue={this.data.startValue}
          endValue={this.data.endValue}
          labels={this.data.labels}
          labelDescriptions={this.data.labelDescriptions}
        />
        {commentItems.length > 0 && (
          <div className="mt-6">
            <AssessmentQuestionStackedTextAnswers
              questionId={this.data.id}
              answers={commentItems}
            />
          </div>
        )}
      </div>
    );
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }
}

class TalentIndicatorQuestion extends BaseQuestion {
  data: TalentIndicatorAssessmentQuestion;

  constructor(data: AssessmentQuestion) {
    super(data);
    if (data.__typename !== "TalentIndicatorAssessmentQuestionNode") {
      throw new Error(
        "Invalid question type in TalentIndicatorQuestion constructor"
      );
    }
    this.data = data;
  }

  labels() {
    return getQuestionLabels(this.data);
  }

  isQuestionAnswered(answer?: AssessmentAnswer | null): boolean {
    return !this.data.isResponseMandatory || isInteger(answer?.integerAnswer);
  }

  getForm({
    answer,
    assessmentType,
    otherParticipantsAnswers,
    onUpdateAnswer,
    formDisabled,
    calibrations,
  }: QuestionFormProps): ReactNode {
    const labels = this.labels();
    const commentItems = otherParticipantsAnswers
      ? otherParticipantsAnswers
          .filter((answer) => !isEmptyValue(answer.comment))
          .map((answer) => ({
            responder: answer.responder,
            text: answer.comment,
            answerValue: isInteger(answer.integerAnswer)
              ? labels[assertNonNull(answer.integerAnswer)]
              : null,
          }))
      : [];
    return (
      <>
        <div className="px-6 pb-6">
          {answer?.isLocked && (
            <div className="mb-6">
              <QuestionLockedByPrecalibration />
            </div>
          )}
          <RangeAssessmentQuestionContent
            disabled={formDisabled}
            onlyRangeOptionsDisabled={answer?.isLocked}
            answer={answer}
            otherParticipantsAnswers={
              assessmentType === AssessmentType.Performance
                ? otherParticipantsAnswers
                : undefined
            }
            question={this.data}
            onChangeAnswer={(newAnswer) => {
              onUpdateAnswer({
                questionId: this.data.id,
                ...answer,
                ...newAnswer,
                textAnswer: newAnswer.textAnswer ?? answer?.textAnswer ?? "{}",
                comment: newAnswer.comment ?? answer?.comment ?? "{}",
              });
            }}
          />
          {commentItems.length > 0 &&
            assessmentType === AssessmentType.Performance && (
              <div className="mt-4">
                <AssessmentQuestionStackedTextAnswers
                  questionId={this.data.id}
                  answers={commentItems}
                />
              </div>
            )}
        </div>
        {calibrations && calibrations.length > 0 && (
          <div className="border-t p-6">
            <QuestionCalibrationSection
              calibrations={calibrations}
              questionLabels={labels}
            />
          </div>
        )}
      </>
    );
  }

  getDelivery({ answer, calibrations }: QuestionDeliveryProps): ReactNode {
    const labels = this.labels();
    return (
      <>
        <div className="px-6 pb-6">
          <RangeAssessmentQuestionContent
            disabled
            answer={answer}
            calibrations={calibrations}
            question={this.data}
          />
        </div>
        {calibrations && calibrations.length > 0 && (
          <div className="border-t p-6">
            <QuestionCalibrationSection
              calibrations={calibrations}
              questionLabels={labels}
            />
          </div>
        )}
      </>
    );
  }

  getDeliveryReadonly({
    answers,
    calibrations,
  }: QuestionDeliveryReadonlyProps): ReactNode {
    const commentsContent = this.getDeliveryComments({ answers });
    const labels = this.labels();
    return (
      <>
        <div className="px-6 pb-6">
          <AssessmentQuestionRangeOptionsCombined
            answers={answers}
            calibrations={calibrations}
            startValue={this.data.startValue}
            endValue={this.data.endValue}
            labels={this.data.labels}
            labelDescriptions={this.data.labelDescriptions}
          />
          {commentsContent && <div className="mt-6">{commentsContent}</div>}
        </div>
        {calibrations && calibrations.length > 0 && (
          <div className="border-t p-6">
            <QuestionCalibrationSection
              calibrations={calibrations}
              questionLabels={labels}
            />
          </div>
        )}
      </>
    );
  }

  getDeliveryComments({ answers }: QuestionDeliveryReadonlyProps): ReactNode {
    const labels = range(this.data.startValue, this.data.endValue + 1).reduce(
      (acc, value, index) => ({
        ...acc,
        [value]: this.data.labels[index] || value.toString(),
      }),
      {} as Record<string, string>
    );
    const commentItems = answers
      .filter((answer) => !isEmptyValue(answer.comment))
      .map((answer) => ({
        responder: answer.responder,
        text: answer.comment,
        answerValue: isInteger(answer.integerAnswer)
          ? labels[assertNonNull(answer.integerAnswer)]
          : null,
      }));
    return commentItems.length > 0 ? (
      <AssessmentQuestionStackedTextAnswers
        questionId={this.data.id}
        answers={commentItems}
      />
    ) : null;
  }
}

const questionClasses: Record<AssessmentQuestionType, QuestionConstructor> = {
  [AssessmentQuestionType.Range]: RangeQuestion,
  [AssessmentQuestionType.Multichoice]: MultichoiceQuestion,
  [AssessmentQuestionType.Text]: TextQuestion,
  [AssessmentQuestionType.Competency]: CompetencyQuestion,
  [AssessmentQuestionType.CompetencyCriteria]: CompetencyCriteriaQuestion,
  [AssessmentQuestionType.Responsibility]: ResponsibilityQuestion,
  [AssessmentQuestionType.IndividualGoal]: IndividualGoalQuestion,
  [AssessmentQuestionType.OverallGoal]: OverallGoalQuestion,
  [AssessmentQuestionType.IndividualRecognitionCoreValue]:
    IndividualRecognitionCoreValueQuestion,
  [AssessmentQuestionType.OverallRecognitionCoreValue]:
    OverallRecognitionCoreValueQuestion,
  [AssessmentQuestionType.Nps]: NpsQuestion,
  [AssessmentQuestionType.TalentIndicator]: TalentIndicatorQuestion,
};

const questionTypenameToEnum: Record<
  AssessmentQuestion["__typename"],
  AssessmentQuestionType
> = {
  RangeAssessmentQuestionNode: AssessmentQuestionType.Range,
  MultiChoiceAssessmentQuestionNode: AssessmentQuestionType.Multichoice,
  TextAssessmentQuestionNode: AssessmentQuestionType.Text,
  CompetencyAssessmentQuestionNode: AssessmentQuestionType.Competency,
  CompetencyCriteriaAssessmentQuestionNode:
    AssessmentQuestionType.CompetencyCriteria,
  ResponsibilityAssessmentQuestionNode: AssessmentQuestionType.Responsibility,
  IndividualGoalAssessmentQuestionNode: AssessmentQuestionType.IndividualGoal,
  OverallGoalAssessmentQuestionNode: AssessmentQuestionType.OverallGoal,
  IndividualRecognitionCoreValueAssessmentQuestionNode:
    AssessmentQuestionType.IndividualRecognitionCoreValue,
  OverallRecognitionCoreValueAssessmentQuestionNode:
    AssessmentQuestionType.OverallRecognitionCoreValue,
  NpsAssessmentQuestionNode: AssessmentQuestionType.Nps,
  TalentIndicatorAssessmentQuestionNode: AssessmentQuestionType.TalentIndicator,
};

export const getQuestionClass = (
  question: AssessmentQuestion,
  opts: QuestionInterfaceOpts
) => {
  return new questionClasses[questionTypenameToEnum[question.__typename]](
    question,
    opts
  );
};
